import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StepProgressIndicator from './ui/StepProgressIndicator';
import WorkflowNavigation from './ui/WorkflowNavigation';
import ReferringDentistSection from '../pages/referral-form/components/ReferringDentistSection';
import PatientInformationSection from '../pages/referral-form/components/PatientInformationSection';
import ToothNumberInput from '../pages/referral-form/components/ToothNumberInput';
import ReferralReasonsSection from '../pages/referral-form/components/ReferralReasonsSection';
import ClinicalNotesSection from '../pages/referral-form/components/ClinicalNotesSection';
import ImagingSection from '../pages/referral-form/components/ImagingSection';
import FileUploadSection from '../pages/referral-form/components/FileUploadSection';
import Footer from './Footer';


const STORAGE_KEY = 'referralFormDraft';

const ReferralFormTemplate = ({ practitionerData = null, slug = null, pageTitle = "Formulaire d'Adressage Endodontique" }) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRestored, setIsRestored] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const [formData, setFormData] = useState({
    referringDentist: {
      fullName: '',
      practiceName: '',
      email: '',
      phone: ''
    },
    patientInfo: {
      lastName: '',
      firstName: '',
      yearOfBirth: '',
      sex: '',
      phone: '',
      email: ''
    },
    toothNumbers: [],
    referralReasons: [],
    clinicalNotes: '',
    imaging: {
      downloadLink: '',
      password: '',
      imagingCenter: ''
    },
    uploadedFiles: []
  });

  const [errors, setErrors] = useState({});

  // Restore form data from localStorage on mount
  useEffect(() => {
    try {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        setFormData(parsedData);
        setIsRestored(true);
      }
    } catch (error) {
      console.error('Error restoring form data:', error);
    }
  }, []);

  // Auto-save form data to localStorage whenever it changes
  useEffect(() => {
    if (isRestored || Object.values(formData)?.some(value => {
      if (typeof value === 'string') return value?.trim() !== '';
      if (Array.isArray(value)) return value?.length > 0;
      if (typeof value === 'object' && value !== null) {
        return Object.values(value)?.some(v => typeof v === 'string' ? v?.trim() !== '' : false);
      }
      return false;
    })) {
      try {
        const { uploadedFiles, ...dataToSave } = formData;
        const dataString = JSON.stringify(dataToSave);
        
        if (dataString?.length > 4 * 1024 * 1024) {
          console.warn('Form data too large for localStorage, skipping auto-save');
          return;
        }
        
        localStorage.setItem(STORAGE_KEY, dataString);
      } catch (error) {
        if (error?.name === 'QuotaExceededError') {
          console.error('localStorage quota exceeded. Clearing old data and retrying...');
          localStorage.removeItem(STORAGE_KEY);
        } else {
          console.error('Error saving form data:', error);
        }
      }
    }
  }, [formData, isRestored]);

  const handleSectionChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev?.[section],
        [field]: value
      }
    }));
    
    if (errors?.[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[field];
        return newErrors;
      });
    }
  };

  const handleToothNumbersChange = (numbers) => {
    setFormData(prev => ({
      ...prev,
      toothNumbers: numbers
    }));
    
    if (errors?.toothNumbers) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.toothNumbers;
        return newErrors;
      });
    }
  };

  const handleReferralReasonsChange = (reasons) => {
    setFormData(prev => ({
      ...prev,
      referralReasons: reasons
    }));
    
    if (errors?.referralReasons) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.referralReasons;
        return newErrors;
      });
    }
  };

  const handleClinicalNotesChange = (notes) => {
    setFormData(prev => ({
      ...prev,
      clinicalNotes: notes
    }));
    
    if (errors?.clinicalNotes) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.clinicalNotes;
        return newErrors;
      });
    }
  };

  const handleAnalysisComplete = (analysisData) => {
    // Store AI analysis results in form state
    console.log('Analysis completed:', analysisData);
    setFormData(prev => ({
      ...prev,
      aiAnalysis: {
        extractedToothNumbers: analysisData?.toothNumbers || [],
        toothNumbersConfidence: analysisData?.toothNumbersConfidence || 0,
        extractedReasons: analysisData?.referralReasons || [],
        referralReasonsConfidence: analysisData?.referralReasonsConfidence || 0,
        urgencyClassification: analysisData?.urgency || null,
        urgencyConfidence: analysisData?.urgencyConfidence || 0,
        urgencyReason: analysisData?.urgencyReason || null
      }
    }));
  };

  const handleFilesChange = (files) => {
    setFormData(prev => ({
      ...prev,
      uploadedFiles: files
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.referringDentist?.fullName?.trim()) {
      newErrors.fullName = 'Le nom est requis';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData?.referringDentist?.email?.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!emailRegex?.test(formData?.referringDentist?.email)) {
      newErrors.email = "Format d'email invalide";
    }

    const phoneRegex = /^[\d\s\-\(\)]+$/;
    if (!formData?.referringDentist?.phone?.trim()) {
      newErrors.phone = 'Le téléphone est requis';
    } else if (!phoneRegex?.test(formData?.referringDentist?.phone)) {
      newErrors.phone = 'Format de téléphone invalide';
    }

    if (!formData?.patientInfo?.lastName?.trim()) {
      newErrors.lastName = 'Le nom du patient est requis';
    }

    if (!formData?.patientInfo?.firstName?.trim()) {
      newErrors.firstName = 'Le prénom du patient est requis';
    }

    if (!formData?.patientInfo?.yearOfBirth) {
      newErrors.yearOfBirth = "L'année de naissance est requise";
    }

    if (formData?.toothNumbers?.length === 0) {
      newErrors.toothNumbers = 'Au moins une dent est requise';
    }

    if (formData?.referralReasons?.length === 0) {
      newErrors.referralReasons = "Au moins un motif d'adressage est requis";
    }

    if (!formData?.clinicalNotes?.trim()) {
      newErrors.clinicalNotes = 'Les notes cliniques sont requises';
    } else if (formData?.clinicalNotes?.trim()?.length < 50) {
      newErrors.clinicalNotes = 'Les notes cliniques doivent contenir au moins 50 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // CRITICAL: Validate practitioner data exists BEFORE any processing
    if (!practitionerData) {
      console.error('❌ CRITICAL ERROR: practitionerData is null or undefined!');
      alert('Erreur critique: Données du praticien manquantes. Veuillez recharger la page.');
      return;
    }

    // Validate practitioner data
    if (!practitionerData?.id) {
      alert('❌ Erreur: Données du praticien manquantes. Veuillez recharger la page.');
      return;
    }

    // Validate form data
    if (!formData?.patientInfo?.firstName || !formData?.patientInfo?.lastName) {
      alert('Erreur: Impossible d\'identifier le patient. Veuillez recharger la page.');
      return;
    }

    setIsSubmitting(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Build submission data with GUARANTEED practitioner_id
      const submissionData = {
        // Form data
        referringDentist: formData?.referringDentist,
        patientInfo: formData?.patientInfo,
        toothNumbers: formData?.toothNumbers,
        referralReasons: formData?.referralReasons,
        clinicalNotes: formData?.clinicalNotes,
        imaging: formData?.imaging,
        uploadedFiles: formData?.uploadedFiles,
        
        // AI Analysis data (if available)
        aiAnalysis: formData?.aiAnalysis || null,
        
        // CRITICAL: Practitioner ID at root level for database insertion
        practitionerId: practitionerData?.id,
        
        // Metadata
        submittedAt: new Date()?.toISOString(),
        referralId: `REF-${Date.now()}`
      };

      // Final validation before navigation
      if (!submissionData?.practitionerId) {
        console.error('❌ CRITICAL: practitionerId is still missing after assignment!');
        alert('Erreur système: Impossible de créer l\'adressage. Contactez le support.');
        setIsSubmitting(false);
        return;
      }

      // Navigate to practitioner-specific PDF preview if slug exists
      const previewRoute = slug ? `/p/${slug}/pdf-preview` : '/pdf-preview';
      
      navigate(previewRoute, {
        state: {
          referralData: submissionData,
          practitionerData: practitionerData
        },
        replace: false
      });
    } catch (error) {
      console.error('❌ Error during form submission:', error);
      alert(`Erreur: ${error?.message || 'Impossible de soumettre le formulaire'}`);
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    const confirmCancel = window.confirm(
      'Êtes-vous sûr de vouloir annuler ? Toutes les données saisies seront perdues.'
    );
    
    if (confirmCancel) {
      localStorage.removeItem(STORAGE_KEY);
      window.location.href = '/';
    }
  };

  return (
    <div className="referral-form-template-page">
      {/* Premium gradient background matching login */}
      <div className="premium-background" />
      
      {/* Brand Header with fade-in animation */}
      <div className="brand-header-form animate-fade-in">
        <div className="header-content-wrapper-form">
          <div className="at-logo-monogram-form">
            {practitionerData?.logoInitials || 'AT'}
          </div>
          <div className="header-text-content-form">
            <h1 className="brand-title-form">
              {practitionerData?.practitionerName || 'Dr Arnaud Touati'}
            </h1>
            <p className="brand-subtitle-form">
              {practitionerData?.practiceName || 'Endodontie exclusive'}
            </p>
          </div>
        </div>
      </div>

      {/* Step Progress Indicator */}
      <div className="animate-fade-in-delay-1">
        <StepProgressIndicator
          currentStep={1}
          totalSteps={3}
          stepLabels={['Formulaire', 'Aperçu', 'Confirmation']}
          completedSteps={[]}
        />
      </div>

      {/* Form Content with staggered fade-in */}
      <div className="form-content-container">
        <div className="form-card animate-fade-in-delay-2">
          <div className="form-header">
            <h2 className="form-title">{pageTitle}</h2>
            <p className="form-subtitle">
              Remplissez ce formulaire pour adresser votre patient
            </p>
          </div>

          <div className="form-sections">
            <ReferringDentistSection
              data={formData?.referringDentist}
              onChange={handleSectionChange}
              errors={errors}
            />

            <div className="section-divider">
              <PatientInformationSection
                data={formData?.patientInfo}
                onChange={handleSectionChange}
                errors={errors}
              />
            </div>

            <div className="section-divider">
              <ToothNumberInput
                selectedTeeth={formData?.toothNumbers}
                onChange={handleToothNumbersChange}
                error={errors?.toothNumbers}
              />
            </div>

            <div className="section-divider">
              <ReferralReasonsSection
                selectedReasons={formData?.referralReasons}
                onChange={handleReferralReasonsChange}
                error={errors?.referralReasons}
              />
            </div>

            <div className="section-divider">
              <ClinicalNotesSection
                notes={formData?.clinicalNotes}
                onChange={handleClinicalNotesChange}
                error={errors?.clinicalNotes}
                toothNumbers={formData?.toothNumbers}
                referralReasons={formData?.referralReasons}
                onAnalysisComplete={handleAnalysisComplete}
                practitionerSlug={slug}
                formData={formData}
              />
            </div>

            <div className="section-divider">
              <ImagingSection
                data={formData?.imaging}
                onChange={handleSectionChange}
                errors={errors}
              />
            </div>

            <div className="section-divider">
              <FileUploadSection
                files={formData?.uploadedFiles}
                onChange={handleFilesChange}
                error={errors?.uploadedFiles}
              />
            </div>
          </div>

          {/* Navigation with improved CTA button */}
          <div className="form-navigation">
            <WorkflowNavigation
              onNext={handleSubmit}
              onPrevious={() => {}}
              onCancel={handleCancel}
              nextLabel="Continuer vers la prévisualisation"
              isNextDisabled={isSubmitting}
              isLoading={isSubmitting}
              showBack={false}
            />
          </div>
        </div>
      </div>

      <Footer practitionerData={practitionerData} />

      <style jsx>{`
        .referral-form-template-page {
          min-height: 100vh;
          background: #1A1A1E;
          display: flex;
          flex-direction: column;
          position: relative;
          overflow-x: hidden;
        }

        /* Premium gradient background matching login page exactly */
        .premium-background {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(to bottom right, rgba(37, 99, 235, 0.15) 0%, #1A1A1E 50%, #1A1A1E 100%);
          z-index: 0;
          pointer-events: none;
        }

        /* Smooth fade-in animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fadeInUp 0.6s ease-out forwards;
        }

        .animate-fade-in-delay-1 {
          opacity: 0;
          animation: fadeInUp 0.6s ease-out 0.15s forwards;
        }

        .animate-fade-in-delay-2 {
          opacity: 0;
          animation: fadeInUp 0.6s ease-out 0.3s forwards;
        }

        .brand-header-form {
          background: rgba(26, 26, 30, 0.8);
          backdrop-filter: blur(40px) saturate(180%);
          -webkit-backdrop-filter: blur(40px) saturate(180%);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          padding: 2rem 1rem;
          position: relative;
          z-index: 10;
          flex-shrink: 0;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
        }

        .header-content-wrapper-form {
          max-width: 1280px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          gap: 1.5rem;
          position: relative;
        }

        .at-logo-monogram-form {
          width: 64px;
          height: 64px;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.1) 100%);
          backdrop-filter: blur(20px);
          color: #FFFFFF;
          border: 1px solid rgba(96, 165, 250, 0.3);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.75rem;
          font-weight: 700;
          letter-spacing: 0.05em;
          flex-shrink: 0;
          box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.6),
            0 0 20px rgba(59, 130, 246, 0.2),
            0 0 0 1px rgba(255, 255, 255, 0.05) inset;
        }

        .header-text-content-form {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .brand-title-form {
          font-size: 1.75rem;
          font-weight: 600;
          color: #FFFFFF;
          margin: 0;
          letter-spacing: -0.03em;
        }

        .brand-subtitle-form {
          font-size: 0.95rem;
          color: #9CA3AF;
          margin: 0;
          letter-spacing: 0.01em;
          font-weight: 400;
        }

        .form-content-container {
          flex: 1;
          max-width: 1280px;
          width: 100%;
          margin: 0 auto;
          padding: 3rem 1rem;
          position: relative;
          z-index: 1;
        }

        .form-card {
          background: rgba(26, 26, 31, 0.6);
          backdrop-filter: blur(40px) saturate(180%);
          -webkit-backdrop-filter: blur(40px) saturate(180%);
          border-radius: 24px;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 
            0 20px 60px rgba(0, 0, 0, 0.5),
            0 0 0 1px rgba(255, 255, 255, 0.05) inset;
          transition: all 0.3s ease;
        }

        .form-card:hover {
          box-shadow: 
            0 24px 70px rgba(0, 0, 0, 0.6),
            0 0 0 1px rgba(255, 255, 255, 0.08) inset;
        }

        .form-header {
          padding: 2.5rem 2rem;
          text-align: center;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
          background: linear-gradient(to bottom, rgba(59, 130, 246, 0.05) 0%, transparent 100%);
        }

        .form-title {
          font-size: 2rem;
          font-weight: 600;
          color: #FFFFFF;
          margin: 0 0 0.75rem 0;
          letter-spacing: -0.03em;
        }

        .form-subtitle {
          font-size: 1.05rem;
          color: #D1D5DB;
          margin: 0;
          font-weight: 400;
        }

        .form-sections {
          padding: 2.5rem;
          display: flex;
          flex-direction: column;
          gap: 0;
        }

        .section-divider {
          border-top: 1px solid rgba(255, 255, 255, 0.08);
          padding-top: 2.5rem;
          margin-top: 2.5rem;
        }

        .form-navigation {
          background: rgba(15, 15, 18, 0.8);
          padding: 1.75rem 2.5rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(20px);
        }

        @media (max-width: 768px) {
          .brand-header-form {
            padding: 1.5rem 1rem;
          }

          .at-logo-monogram-form {
            width: 52px;
            height: 52px;
            font-size: 1.5rem;
            border-radius: 12px;
          }

          .brand-title-form {
            font-size: 1.5rem;
          }

          .brand-subtitle-form {
            font-size: 0.875rem;
          }

          .form-content-container {
            padding: 2rem 1rem;
          }

          .form-header {
            padding: 2rem 1.5rem;
          }

          .form-title {
            font-size: 1.5rem;
          }

          .form-sections {
            padding: 2rem 1.5rem;
          }

          .section-divider {
            padding-top: 2rem;
            margin-top: 2rem;
          }

          .form-navigation {
            padding: 1.25rem 1.5rem;
          }
        }

        @media (max-width: 640px) {
          .brand-header-form {
            padding: 1rem;
          }

          .form-content-container {
            padding: 1rem;
          }

          .form-sections {
            padding: 1.5rem 1rem;
          }

          .form-navigation {
            padding: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default ReferralFormTemplate;